import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../services/api";
import PharmacySupplierLedger from "./PharmacySupplierLedger";
import PharmacySupplierBillPayment from "./PharmacySupplierBillPayment";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";

interface PharmacySupplier {
  id: number;
  name: string;
  mobile?: string;
  balance_due?: number;
}

const formatINR = (value: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function PharmacySuppliersPanel() {
  const { showToast } = useToast();
  const [suppliers, setSuppliers] = useState<PharmacySupplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<PharmacySupplier | null>(null);
  const [paySupplier, setPaySupplier] = useState<PharmacySupplier | null>(null);
  const [billModal, setBillModal] = useState<{ open: boolean; supplier: PharmacySupplier | null }>({ open: false, supplier: null });
  const [billForm, setBillForm] = useState({
    invoice_no: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    paid_amount: "",
    receipt_number: ""
  });
  const [supplierModal, setSupplierModal] = useState<{ open: boolean; data: PharmacySupplier | null }>({ open: false, data: null });
  const [supplierForm, setSupplierForm] = useState({ name: "", mobile: "" });
  const [existingBills, setExistingBills] = useState<{ id: number, invoice_no: string, total: number }[]>([]);
  const [reload, setReload] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    apiGet("/api/pharmacy/suppliers")
      .then((res: unknown) => setSuppliers(Array.isArray(res) ? (res as PharmacySupplier[]) : []))
      .catch(() => setSuppliers([]));
  }, [reload]);

  useEffect(() => {
    if (suppliers.length > 0) {
      if (selectedSupplier) {
        // Sync selectedSupplier with the updated object from the refreshed list
        const updated = suppliers.find(s => s.id === selectedSupplier.id);
        if (updated && updated !== selectedSupplier) {
          setSelectedSupplier(updated);
        }
      } else {
        setSelectedSupplier(suppliers[0]);
      }
    }
  }, [suppliers]);

  useEffect(() => {
    if (!billModal.open || !billModal.supplier?.id) {
      setExistingBills([]);
      return;
    }
    apiGet(`/api/pharmacy/supplier/${billModal.supplier.id}/bills?include_paid=true`)
      .then((res: unknown) => setExistingBills(Array.isArray(res) ? (res as any[]) : []))
      .catch(() => setExistingBills([]));
  }, [billModal.open, billModal.supplier?.id]);

  const openSupplierModal = (supplier: PharmacySupplier | null = null) => {
    setSupplierForm({
      name: supplier?.name ?? "",
      mobile: supplier?.mobile ?? ""
    });
    setSupplierModal({ open: true, data: supplier });
  };

  const handleManualBillSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!billModal.supplier || !billForm.invoice_no || !billForm.amount) return;
    try {
      await apiPost("/api/pharmacy/supplier-bill/add", {
        supplier_id: billModal.supplier.id,
        invoice_no: billForm.invoice_no,
        amount: parseFloat(billForm.amount),
        bill_date: billForm.date,
        paid_amount: parseFloat(billForm.paid_amount) || 0,
        receipt_number: billForm.receipt_number
      });
      setBillModal({ open: false, supplier: null });
      setBillForm({
        invoice_no: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        paid_amount: "",
        receipt_number: ""
      });
      setReload(p => !p);
      showToast({ message: "Manual bill recorded successfully", type: "success" });
    } catch {
      showToast({ message: "Failed to record manual bill", type: "error" });
    }
  };

  const saveSupplier = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!supplierForm.name.trim()) return showToast({ message: "Name is required", type: "error" });
    try {
      await apiPost("/api/pharmacy/suppliers", {
        id: supplierModal.data?.id,
        name: supplierForm.name,
        mobile: supplierForm.mobile
      });
      setSupplierModal({ open: false, data: null });
      setReload((p) => !p);
      showToast({ message: "Supplier saved successfully", type: "success" });
    } catch {
      showToast({ message: "Failed to save pharmacy supplier", type: "error" });
    }
  };

  const deleteSupplier = async (id: number, name: string) => {
    if (!window.confirm(`Delete pharmacy supplier "${name}"?`)) return;
    try {
      await apiPost("/api/pharmacy/suppliers/delete", { supplier_id: id });
      if (selectedSupplier?.id === id) setSelectedSupplier(null);
      setReload((p) => !p);
      showToast({ message: "Supplier deleted", type: "info" });
    } catch {
      showToast({ message: "Cannot delete: supplier may have linked stock entries.", type: "error" });
    }
  };

  const normalizedFilter = filter.trim().toLowerCase();
  const filteredSuppliers = suppliers
    .filter((s) => {
      if (!normalizedFilter) return true;
      const name = (s.name || "").toLowerCase();
      const mobile = (s.mobile || "").toLowerCase();
      return name.includes(normalizedFilter) || mobile.includes(normalizedFilter);
    })
    .sort((a, b) => (Number(b.balance_due || 0) - Number(a.balance_due || 0)) || a.name.localeCompare(b.name));

  const totalDue = filteredSuppliers.reduce((sum, s) => sum + Number(s.balance_due || 0), 0);

  return (
    <div className="mod-standalone-window mod-padding-mobile" style={{ minHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', padding: '0' }}>
      <div className="mod-window-header" style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>💊 Pharmacy Suppliers</h2>
            <div style={{ display: 'flex', gap: '24px', marginTop: '12px' }}>
              <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Suppliers: <strong style={{ color: '#0f172a' }}>{filteredSuppliers.length}</strong></span>
              <span style={{ fontSize: '0.9rem', color: '#64748b' }}>Total Due: <strong style={{ color: '#dc2626' }}>{formatINR(totalDue)}</strong></span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Button variant="outline" onClick={() => setReload((p) => !p)}>
              ↻ Refresh
            </Button>
            <Button variant="primary" onClick={() => openSupplierModal(null)}>
              + Add Pharmacy Supplier
            </Button>
          </div>
        </div>
      </div>
      <div className="mod-split-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 350px) 1fr', gap: '24px', padding: '24px 32px', flex: 1, alignItems: 'start' }}>
        <div className="mod-card mod-height-auto" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)' }}>
          <div style={{ padding: '16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#0f172a' }}>Supplier List</h4>
            <input
              className="mod-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search name / mobile…"
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {suppliers.length === 0 ? (
              <div className="mod-empty-state" style={{ margin: '20px 0' }}>No pharmacy suppliers. Add one to use in Stock Entry.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredSuppliers.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => setSelectedSupplier(s)}
                    className={`apexo-folder-card ${selectedSupplier?.id === s.id ? "active" : ""}`}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <strong style={{ color: '#0f172a', fontSize: '1.05rem' }}>{s.name}</strong>
                      <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{s.mobile || "—"}</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: Number(s.balance_due || 0) > 0 ? '#dc2626' : '#10b981', marginTop: '4px' }}>
                        Due: {formatINR(Number(s.balance_due || 0))}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openSupplierModal(s); }} style={{ padding: '4px 8px', color: '#64748b' }}>✏️</Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteSupplier(s.id, s.name); }} style={{ padding: '4px 8px', color: '#dc2626' }}>🗑️</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedSupplier && (
            <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <Button variant="secondary" onClick={() => setPaySupplier(selectedSupplier)} style={{ width: '100%', justifyContent: 'center' }}>
                💳 Pay Bills
              </Button>
              <Button
                variant="primary"
                onClick={() => setBillModal({ open: true, supplier: selectedSupplier })}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                + Add Bill (Manual)
              </Button>
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedSupplier ? (
            <PharmacySupplierLedger
              supplier={selectedSupplier}
              onPayBill={() => setPaySupplier(selectedSupplier)}
              refreshTrigger={reload}
            />
          ) : (
            <div className="mod-empty-state" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>👈 Select a pharmacy supplier</div>
          )}
        </div>
      </div>

      {supplierModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSupplierModal({ open: false, data: null })}>
          <form className="mod-card" style={{ maxWidth: '400px', width: '100%', padding: '24px' }} onClick={e => e.stopPropagation()} onSubmit={saveSupplier}>
            <h3 style={{ margin: '0 0 20px 0', color: '#0f172a', fontSize: '1.25rem' }}>{supplierModal.data ? "Edit Pharmacy Supplier" : "Add Pharmacy Supplier"}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Name</label>
                <input
                  className="mod-input"
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Mobile / Contact</label>
                <input
                  className="mod-input"
                  value={supplierForm.mobile}
                  onChange={(e) => setSupplierForm({ ...supplierForm, mobile: e.target.value })}
                />
              </div>
            </div>
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button variant="ghost" onClick={() => setSupplierModal({ open: false, data: null })}>Cancel</Button>
              <Button type="submit" variant="primary">Save Configuration</Button>
            </div>
          </form>
        </div>
      )}

      {billModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setBillModal({ open: false, supplier: null })}>
          <form className="mod-card" style={{ maxWidth: '500px', width: '100%', padding: '24px' }} onClick={e => e.stopPropagation()} onSubmit={handleManualBillSave}>
            <h3 style={{ margin: '0 0 20px 0', color: '#0f172a', fontSize: '1.25rem' }}>Add Manual Bill for {billModal.supplier?.name}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Invoice No / Ref</label>
                <input
                  type="text"
                  list="existing-bills-ledger"
                  className="mod-input"
                  value={billForm.invoice_no}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillForm({ ...billForm, invoice_no: val });
                    const match = existingBills.find(b => b.invoice_no === val);
                    if (match) setBillForm(prev => ({ ...prev, amount: String(match.total) }));
                  }}
                  placeholder="e.g. INV-123"
                />
                <datalist id="existing-bills-ledger">
                  {existingBills.map(b => (
                    <option key={b.id} value={b.invoice_no}>₹{b.total}</option>
                  ))}
                </datalist>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Bill Amount (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="mod-input"
                  value={billForm.amount}
                  onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Amount Paid (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="mod-input"
                    value={billForm.paid_amount}
                    onChange={(e) => setBillForm({ ...billForm, paid_amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Receipt #</label>
                  <input
                    type="text"
                    className="mod-input"
                    value={billForm.receipt_number}
                    onChange={(e) => setBillForm({ ...billForm, receipt_number: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#334155', fontSize: '0.9rem' }}>Bill Date</label>
                <input
                  type="date"
                  className="mod-input"
                  value={billForm.date}
                  onChange={(e) => setBillForm({ ...billForm, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button variant="ghost" onClick={() => setBillModal({ open: false, supplier: null })}>Cancel</Button>
              <Button type="submit" variant="primary">Record Bill</Button>
            </div>
          </form>
        </div>
      )}

      {paySupplier && (
        <PharmacySupplierBillPayment
          supplier={paySupplier}
          onClose={() => setPaySupplier(null)}
          onPaid={() => { setPaySupplier(null); setReload((p) => !p); }}
        />
      )}
    </div>
  );
}
